import { registerMockAPI } from '~/mocks/mockApis';

import { useQuery } from '@tanstack/react-query';

import { axiosInstance } from '~/services';

import { API_ENDPOINT } from '~/constants';

import { receiveKeys } from './keys';

import type { TypeErrorResponseData } from '~/types/common.types';
import type { TypeGetReturnDisposalIssueItemsResponse, TypeReturnDisposalIssueItemApi } from '~/types/receive.types';

/**
 * [입고관리 > 회송/폐기 관리] 처리신청 상품정보 미리보기 조회 API
 * @note 배열 파라미터는 반복 키(`inspectionIds=1&inspectionIds=2`)로 직렬화한다.
 */
const fetchReturnDisposalIssueItems = async (inspectionIds: number[]): Promise<TypeReturnDisposalIssueItemApi[]> => {
  const { data } = await axiosInstance.get<TypeGetReturnDisposalIssueItemsResponse>(
    API_ENDPOINT.RECEIVE.GET_RETURN_DISPOSAL_ISSUE_ITEMS,
    { params: { inspectionIds }, paramsSerializer: { indexes: null } }
  );

  return data.data.issueItems;
};

type TypeUseGetReturnDisposalIssueItemsQueryParams = {
  inspectionIds: number[];
  enabled?: boolean;
};

/** [입고관리 > 회송/폐기 관리] 처리신청 상품정보 미리보기 조회 Query Hook (모달 오픈 시에만 enabled) */
export const useGetReturnDisposalIssueItemsQuery = ({
  inspectionIds,
  enabled = true,
}: TypeUseGetReturnDisposalIssueItemsQueryParams) => {
  return useQuery({
    queryKey: receiveKeys.returnDisposalIssueItems(inspectionIds),
    queryFn: () => fetchReturnDisposalIssueItems(inspectionIds),
    enabled: enabled && inspectionIds.length > 0,
    staleTime: 0,
    gcTime: 1000 * 30,
  });
};

/** [입고관리 > 회송/폐기 관리] 상품정보 미리보기 조회 Mock (파라미터 무시·시나리오 고정 응답) */
registerMockAPI<TypeGetReturnDisposalIssueItemsResponse | TypeErrorResponseData>({
  method: 'get',
  endpoint: API_ENDPOINT.RECEIVE.GET_RETURN_DISPOSAL_ISSUE_ITEMS,
  label: '회송폐기관리/상품정보 미리보기 조회',
  scenarios: [
    {
      name: '정상 응답 (회수-직배)',
      response: {
        data: {
          issueItems: [
            {
              inspectionId: 9,
              purchaseOrderCode: 'RO202606260096',
              goodsCode: 'PD1122334455',
              goodsName: '오염 - 신선 샐러드 채소 모둠',
              issueType: '오염',
              issueQty: 24,
              weightKg: 0.72,
              issuedWarehouse: 'TP01',
              issuedWarehouseName: '평택상온',
              manufactureDate: null,
              expirationDate: '2026-05-31',
              processMethod: 'APPLICATION_AWAITING',
              processStatus: 'PENDING_INPUT',
            },
            {
              inspectionId: 11,
              purchaseOrderCode: 'RO202606260097',
              goodsCode: 'PD2233445566',
              goodsName: '파손 - 유기농 사과 1kg',
              issueType: '파손',
              issueQty: 50,
              weightKg: 50,
              issuedWarehouse: 'TP01',
              issuedWarehouseName: '평택상온',
              manufactureDate: '2026-05-01',
              expirationDate: '2026-06-30',
              processMethod: 'APPLICATION_AWAITING',
              processStatus: 'PENDING_INPUT',
            },
          ],
        },
      },
    },
    {
      name: '서버 에러 (500)',
      response: { error: { code: 500, message: '상품 정보를 불러오지 못했습니다.' } },
      status: 500,
    },
  ],
});
